import { CheckCircle2, XCircle, AlertTriangle, WifiOff, Loader2, ScanLine } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { AthletePhoto } from "@/components/common";
import { AdmittedCounter } from "@/components/AdmittedCounter";

const THEMES = {
  admitted: { bg: "bg-emerald-600", title: "ATLETA AMMESSO", Icon: CheckCircle2 },
  registered: { bg: "bg-emerald-700", title: "PARTECIPAZIONE REGISTRATA", Icon: CheckCircle2 },
  denied: { bg: "bg-red-600", title: "ATLETA NON AMMESSO", Icon: XCircle },
  already: { bg: "bg-amber-500", title: "PARTECIPAZIONE GIÀ REGISTRATA", Icon: AlertTriangle },
  unavailable: { bg: "bg-slate-800", title: "VERIFICA NON DISPONIBILE", Icon: WifiOff },
};

const Line = ({ label, value, mono }) => (
  <div className="flex justify-between gap-4 border-b border-white/15 py-2 text-sm last:border-none">
    <span className="text-white/75">{label}</span>
    <span className={`text-right font-semibold ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
  </div>
);

export function ScanResult({ result, busy, onRegister, onNext, onClose, counter }) {
  const t = THEMES[result.kind];
  const d = result.data || {};
  return (
    <div className={`fixed inset-0 z-50 flex flex-col overflow-y-auto ${t.bg} text-white`} data-testid={`scan-result-${result.kind}`}>
      <div className="mx-auto flex w-full max-w-lg flex-1 flex-col px-5 py-8 result-pop">
        <div className="flex flex-col items-center text-center">
          <t.Icon className="h-24 w-24 sm:h-28 sm:w-28" strokeWidth={2.2} />
          <div className="mt-4 font-display text-3xl font-black tracking-tight sm:text-4xl" data-testid="scan-result-title">{t.title}</div>
          {d.event && <div className="mt-1 text-sm text-white/80" data-testid="scan-result-event">{d.event.name}</div>}
        </div>
        {d.athlete && (
          <div className="mt-6 flex items-center gap-4 rounded-2xl bg-black/15 p-4">
            <AthletePhoto athleteId={d.athlete.id} hasPhoto={d.athlete.has_photo} name={d.athlete.full_name} className="h-24 w-20" rounded="rounded-xl" />
            <div>
              <div className="font-display text-2xl font-bold leading-tight" data-testid="scan-result-athlete-name">{d.athlete.full_name}</div>
              <div className="font-mono text-sm text-white/80">Tessera {d.athlete.membership_number}</div>
            </div>
          </div>
        )}
        {d.license && (
          <div className="mt-4 rounded-2xl bg-black/10 px-4 py-2">
            <Line label="Numero licenza" value={d.license.number} mono />
            <Line label="Disciplina" value={d.license.discipline} />
            <Line label="Categoria" value={d.license.categories?.join(", ")} />
            <Line label="Scadenza" value={fmtDate(d.license.expiry_date)} />
          </div>
        )}
        {(d.reasons?.length > 0 || result.message) && (
          <div className="mt-4 rounded-2xl bg-black/20 p-4" data-testid="scan-result-reasons">
            <div className="mb-2 text-xs font-bold uppercase tracking-widest text-white/80">Motivazione</div>
            {result.message && <div className="text-lg font-semibold">{result.message}</div>}
            {d.reasons?.map((r) => <div key={r} className="text-lg font-semibold">• {r}</div>)}
          </div>
        )}
        {counter && ["admitted", "registered", "already"].includes(result.kind) && <AdmittedCounter count={counter.count} max={counter.max} variant="result" />}
        {result.kind === "unavailable" && <div className="mt-4 text-center text-sm text-white/80">La licenza NON può essere considerata valida senza la verifica sul Registro Nazionale.</div>}
        <div className="mt-auto space-y-3 pt-8">
          {result.kind === "admitted" && (
            <button onClick={onRegister} disabled={busy} className="flex h-16 w-full items-center justify-center gap-2 rounded-2xl bg-white text-lg font-black tracking-wide text-emerald-700 shadow-lg transition-transform active:scale-[0.98] disabled:opacity-70" data-testid="register-participation-btn">
              {busy ? <Loader2 className="h-6 w-6 animate-spin" /> : "REGISTRA PARTECIPAZIONE"}
            </button>
          )}
          <button onClick={onNext} className="flex h-14 w-full items-center justify-center gap-2 rounded-2xl border-2 border-white/70 text-base font-bold transition-colors hover:bg-white/10" data-testid="scan-next-btn">
            <ScanLine className="h-5 w-5" />SCANSIONA PROSSIMO
          </button>
          <button onClick={onClose} className="w-full py-2 text-sm text-white/80 underline-offset-4 hover:underline" data-testid="scan-result-close-btn">Chiudi</button>
        </div>
      </div>
    </div>
  );
}
